import React from "react";
import { Link } from "react-router-dom";
import InnerHero from "../components/InnerHero";
import { Beaker, Building2, CheckCircle2, Sparkles, Users } from "lucide-react";

const associations = [
  {
    id: "ieee",
    title: "IEEE",
    name: "Institute of Electrical and Electronics Engineers",
    kicker: "Professional Body",
    body: "Student and faculty engagement with engineering communities, technical talks, workshops, and project showcases.",
  },
  {
    id: "csi",
    title: "CSI",
    name: "Computer Society of India",
    kicker: "Professional Body",
    body: "Computer Society of India activities supporting coding culture, industry interaction, and technology awareness.",
  },
  {
    id: "acm",
    title: "ACM",
    name: "Association for Computing Machinery",
    kicker: "Professional Body",
    body: "Association for Computing Machinery initiatives for programming, research exposure, and peer learning.",
  },
  {
    id: "iste",
    title: "ISTE",
    name: "Indian Society for Technical Education",
    kicker: "Professional Body",
    body: "Indian Society for Technical Education programmes focused on teaching, learning, and professional development.",
  },
  {
    id: "iei",
    title: "IEI",
    name: "Institution of Engineers (India)",
    kicker: "Professional Body",
    body: "Institution of Engineers activities connecting students with professional engineering practice.",
  },
  {
    id: "bites",
    title: "BITES",
    name: "Board for IT Education Standards",
    kicker: "Professional Body",
    body: "Board for IT Education Standards programmes linking academic learning with industry expectations.",
  },
  {
    id: "iic",
    title: "IIC",
    name: "Institution's Innovation Council",
    kicker: "Innovation Hub",
    body: "Institution Innovation Council initiatives for entrepreneurship, innovation, prototyping, and startup awareness.",
  },
];

const Section = ({ id, title, icon: Icon, children }) => (
  <section id={id} className="scroll-mt-32">
    <h3
      className="text-2xl md:text-3xl text-brand font-semibold flex items-center gap-2 mb-5"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      {Icon && <Icon size={22} />} {title}
    </h3>
    {children}
  </section>
);

const Associations = () => {
  const navItems = [
    ["overview", "Overview"],
    ["professional-bodies", "Professional Bodies"],
    ["innovation-hub", "Innovation Hub"],
  ];

  const professionalBodies = associations.filter((item) => item.kicker === "Professional Body");
  const innovationHubs = associations.filter((item) => item.kicker === "Innovation Hub");

  return (
    <main>
      <InnerHero
        title="Association / Innovation Hub"
        image="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?w=2400"
        breadcrumbs={[{ label: "Associations & Innovation Hub" }]}
      />

      <section className="bg-surface-alt/60 border-b border-brand/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap gap-x-6 gap-y-2 text-xs font-sans-ui font-semibold tracking-wider text-brand">
          {navItems.map(([h, l]) => (
            <a key={h} href={`#${h}`} className="uppercase hover:underline">
              {l}
            </a>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-12">
            <Section id="overview" title="Associations & Innovation Hub" icon={Users}>
              <p className="text-[#2a2a2a]/85 leading-relaxed text-[15.5px]">
                Professional bodies and innovation platforms at KLS VDIT support student learning through technical communities, industry interaction, peer learning, leadership opportunities, and entrepreneurship-oriented activities.
              </p>
              <div className="grid sm:grid-cols-3 gap-3 mt-5">
                {[
                  ["Professional Bodies", professionalBodies.length],
                  ["Innovation Hubs", innovationHubs.length],
                  ["Student Platforms", associations.length],
                ].map(([label, value]) => (
                  <div key={label} className="bg-[#7a1d2c] p-4 border border-[#7a1d2c]">
                    <div
                      className="text-3xl text-white font-semibold"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {value}
                    </div>
                    <div className="text-xs uppercase text-white/80 font-sans-ui tracking-widest mt-1">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="professional-bodies" title="Professional Bodies" icon={Building2}>
              <div className="grid md:grid-cols-2 gap-4">
                {professionalBodies.map((item) => (
                  <Link
                    key={item.id}
                    to={`/associations/${item.id}`}
                    className="bg-white border border-brand/15 p-5 card-hover block"
                  >
                    <span className="text-xs tracking-[0.18em] text-brand font-sans-ui font-semibold uppercase">
                      {item.kicker}
                    </span>
                    <h4
                      className="text-2xl text-brand font-semibold mt-2"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {item.title}
                    </h4>
                    <p className="text-sm text-[#2a2a2a]/85 leading-relaxed mt-2">
                      {item.body}
                    </p>
                  </Link>
                ))}
              </div>
            </Section>

            <Section id="innovation-hub" title="Innovation Hub" icon={Sparkles}>
              <div className="grid md:grid-cols-2 gap-4">
                {innovationHubs.map((item) => (
                  <Link
                    key={item.id}
                    to={`/associations/${item.id}`}
                    className="bg-white border border-brand/15 p-5 card-hover block"
                  >
                    <span className="text-xs tracking-[0.18em] text-brand font-sans-ui font-semibold uppercase">
                      {item.kicker}
                    </span>
                    <h4
                      className="text-2xl text-brand font-semibold mt-2"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {item.title}
                    </h4>
                    <p className="text-sm text-[#2a2a2a]/85 leading-relaxed mt-2">
                      {item.body}
                    </p>
                  </Link>
                ))}
              </div>
            </Section>
          </div>

          <aside className="space-y-5">
            <div className="bg-white border border-brand/15 p-5 lg:sticky lg:top-36 self-start">
              <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
                Quick Links
              </h4>
              <ul className="mt-3 space-y-1.5">
                {associations.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/associations/${item.id}`}
                      className="text-sm text-[#2a2a2a] hover:text-brand flex items-start gap-1 leading-snug"
                    >
                      <Beaker size={12} className="text-brand flex-shrink-0 mt-0.5" />
                      <span>{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-5 border-t border-brand/10">
                <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                  Focus Areas
                </h4>
                <ul className="space-y-2 text-sm text-[#2a2a2a]">
                  {[
                    "Technical communities",
                    "Industry interaction",
                    "Leadership and volunteering",
                    "Innovation and entrepreneurship",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle2 size={14} className="text-brand flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default Associations;
